import { Component, EventEmitter, Inject, Input, OnDestroy, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { UploadService } from '@wb/core/services/upload.service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Subject } from 'rxjs';
import { environment } from '../../../../environments/environment';

dayjs.extend(utc);

@Component({
  selector: 'app-file-upload-dialog',
  templateUrl: './file-upload-dialog.component.html',
  styleUrls: ['./file-upload-dialog.component.sass'],
})
export class FileUploadComponent implements OnDestroy {
  @Output() save: EventEmitter<void> = new EventEmitter();

  @Input() activeAccountName: string | undefined;

  uploadValid: boolean = false;

  displayImageUpload: boolean = true;

  fileName = '';

  noFileName = '';

  uploadLocation = '';

  bucket: string = '';

  directory: string = '';

  overrideFileName: string | undefined;

  file: File | null = null;

  isLoadingData = false;

  warning: boolean;

  translate: TranslateService | undefined;

  isVideo = false;

  private destroyed$: Subject<void> = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      isVideo: boolean;
      imageUpload: boolean;
      bucket: string;
      directory: string;
      overrideFileName: string;
      warning: boolean;
    },
    public dialogRef: MatDialogRef<FileUploadComponent>,
    private uploadService: UploadService,
    translate: TranslateService,
  ) {
    this.displayImageUpload = data.imageUpload;
    this.isVideo = data.isVideo;
    this.bucket = data.bucket;
    this.directory = data.directory;
    this.translate = translate;
    this.overrideFileName = data.overrideFileName;
    this.warning = !!data.warning;
    this.fileName = translate.instant('fileUpload.noFile');
    this.noFileName = translate.instant('fileUpload.noFile');
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  previewFile(fileInputEvent: any) {
    this.isLoadingData = true;
    this.file = fileInputEvent.target.files[0];
    this.fileName = fileInputEvent.target.files[0].name;

    if (this.displayImageUpload) {
      this.checkFileValid(fileInputEvent);
      if (this.uploadValid) {
        //@ts-ignore
        document.getElementById('preview').src = window.URL.createObjectURL(fileInputEvent.target.files[0]);
      } else {
        //@ts-ignore
        document.getElementById('preview').src = 'assets/images/img-thumbnail.svg';
      }
    } else {
      this.checkFileValid(fileInputEvent);
      if (!this.uploadValid) {
        this.fileName = this.noFileName;
      }
    }
    this.isLoadingData = false;
  }

  checkFileValid(fileInputEvent: any) {
    //file size in bytes
    const sevenMegabytes = 7440930;
    const fiftyMegabytes = 52428800;

    let validSize = this.isVideo
      ? fileInputEvent.target.files[0].size <= fiftyMegabytes
      : fileInputEvent.target.files[0].size <= sevenMegabytes;
    let type = fileInputEvent.target.files[0].type;
    let validType: boolean;

    if (this.displayImageUpload) {
      validType = type === 'image/jpg' || type === 'image/png' || type === 'image/jpeg';
    } else {
      validType = this.isVideo ? type === 'video/mp4' : type === 'text/plain' || type === 'application/pdf';
    }

    this.uploadValid = validType && validSize && this.file !== undefined;

    document.getElementById('sizeValid')?.classList.remove('in');
    document.getElementById('typeValid')?.classList.remove('in');

    if (validSize) {
      document.getElementById('sizeValid')?.classList.add('in');
    }

    if (validType) {
      document.getElementById('typeValid')?.classList.add('in');
    }
  }

  async sendFile() {
    const date = dayjs().utc().format('YYYY-MM-DD_HH.mm.ss');
    const fileNameSplit = this.fileName.split('.');

    const fileName = this.overrideFileName ? this.overrideFileName : fileNameSplit[0];
    const fileExtension = fileNameSplit[1];
    const newFileName = `${fileName}_${date}`;
    const filePath = `${environment.stage}/${this.directory}/${newFileName}.${fileExtension}`;

    if (this.uploadValid) {
      this.isLoadingData = true;
      const fileLocationParams = {
        bucketName: this.bucket,
        directory: this.directory,
        fileName: newFileName,
        fileExtension: fileExtension,
      };
      const response = await this.uploadService.uploadFile(this.file as File, fileLocationParams);
      this.uploadLocation = response.message;
      this.isLoadingData = false;
      this.dialogRef.close({
        uploadLocation: this.uploadLocation,
        uploadLocationPublic: `${environment.aws.cdnUri}/${filePath}`,
        filePath: filePath,
        fileName: this.fileName,
      });
    }
  }
}
